import { CheckCircle2, XCircle } from "lucide-react";

/* WSAVA-style 1–9 body condition scale. */
export const BCS_LABELS = {
  1: "Emaciated", 2: "Very thin", 3: "Thin", 4: "Lean", 5: "Ideal",
  6: "Overweight", 7: "Heavy", 8: "Obese", 9: "Grossly obese",
};
const bandOf = (n) => (n <= 3 ? "under" : n <= 5 ? "ideal" : "over");
const BAND = {
  under: { color: "#d97706", text: "text-amber-600" },
  ideal: { color: "#15803d", text: "text-emerald-600" },
  over: { color: "#dc2626", text: "text-rose-600" },
};
const SPECIES_FILL = { cat: "#c5ccd6", dog: "#e4c89a" };

/* ---- top-down (from above) silhouette path, head at top ---- */
const topDownPath = (bcs) => {
  const cx = 100;
  const t = (bcs - 1) / 8;
  const chest = 34 + 14 * t;
  const waist = 15 + 42 * t;
  const hip = 30 + 16 * t;
  const yNeck = 60, yChest = 102, yWaist = 154, yHip = 198, yRear = 240;
  return [
    `M ${cx} ${yNeck}`,
    `C ${cx + chest * 0.7} ${yNeck + 2}, ${cx + chest} ${yChest - 24}, ${cx + chest} ${yChest}`,
    `C ${cx + chest} ${yChest + 30}, ${cx + waist} ${yWaist - 24}, ${cx + waist} ${yWaist}`,
    `C ${cx + waist} ${yWaist + 24}, ${cx + hip} ${yHip - 24}, ${cx + hip} ${yHip}`,
    `C ${cx + hip} ${yHip + 28}, ${cx + hip * 0.5} ${yRear - 4}, ${cx} ${yRear}`,
    `C ${cx - hip * 0.5} ${yRear - 4}, ${cx - hip} ${yHip + 28}, ${cx - hip} ${yHip}`,
    `C ${cx - hip} ${yHip - 24}, ${cx - waist} ${yWaist + 24}, ${cx - waist} ${yWaist}`,
    `C ${cx - waist} ${yWaist - 24}, ${cx - chest} ${yChest + 30}, ${cx - chest} ${yChest}`,
    `C ${cx - chest} ${yChest - 24}, ${cx - chest * 0.7} ${yNeck + 2}, ${cx} ${yNeck}`,
    "Z",
  ].join(" ");
};

/* ---- lateral (side) silhouette path, head at left ---- */
const lateralPath = (bcs) => {
  const sag = Math.max(-22, Math.min(22, (bcs - 5) * 5)); // belly: -tuck up / +sag down
  const hump = Math.max(0, bcs - 5) * 2.4; // loin fat over the back
  const xHead = 42, xTail = 198;
  return [
    `M ${xHead} 70`,
    `C 48 50, 62 46, 80 48`,
    `C 120 ${50 - hump}, 165 ${50 - hump}, ${xTail} 56`,
    `C 210 64, 206 80, 194 88`,
    `C 178 98, 162 ${98 + sag}, 138 ${102 + sag}`,
    `C 112 ${106 + sag}, 88 ${106 + sag}, 72 96`,
    `C 60 92, 52 86, ${xHead} 82`,
    `C 34 78, 34 74, ${xHead} 70`,
    "Z",
  ].join(" ");
};

function Ears({ species, cx, cy }) {
  if (species === "cat")
    return (
      <>
        <path d={`M ${cx - 16} ${cy - 16} l 6 -16 l 10 12 Z`} className="ear" />
        <path d={`M ${cx + 16} ${cy - 16} l -6 -16 l -10 12 Z`} className="ear" />
      </>
    );
  return (
    <>
      <ellipse cx={cx - 19} cy={cy - 4} rx="7" ry="13" className="ear" transform={`rotate(-24 ${cx - 19} ${cy - 4})`} />
      <ellipse cx={cx + 19} cy={cy - 4} rx="7" ry="13" className="ear" transform={`rotate(24 ${cx + 19} ${cy - 4})`} />
    </>
  );
}

/* Top-down view with rib / spine / fat cues that fade in by score. */
function TopDownView({ bcs, species, stroke, fill, cues = true, ghostPath }) {
  const t = (bcs - 1) / 8;
  const waist = 15 + 42 * t;
  const ribAlpha = Math.max(0, (4 - bcs) / 3); // thin → ribs show
  const fatAlpha = Math.max(0, (bcs - 5) / 4); // fat → pads show
  const cx = 100;
  return (
    <svg viewBox="0 0 200 268" className="w-full h-full [&_.ear]:fill-current" style={{ color: fill }}>
      <Ears species={species} cx={cx} cy={44} />
      <path d={topDownPath(bcs)} fill={fill} stroke={stroke} strokeWidth="3" strokeLinejoin="round" />
      <ellipse cx={cx} cy={44} rx="21" ry="23" fill={fill} stroke={stroke} strokeWidth="3" />
      {cues && (
        <>
          {/* spine */}
          <line x1={cx} y1="74" x2={cx} y2="230" stroke="#7c5a2a" strokeWidth="2" opacity={0.18 + ribAlpha * 0.4} />
          {/* ribs over the chest (thin patients) */}
          {ribAlpha > 0.02 && [0, 1, 2].map((i) => {
            const y = 96 + i * 12;
            return (
              <path key={i} d={`M ${cx - 26} ${y} Q ${cx} ${y + 7} ${cx + 26} ${y}`}
                fill="none" stroke="#7c5a2a" strokeWidth="2" opacity={ribAlpha * 0.8} />
            );
          })}
          {/* fat pads at the flanks (overweight patients) */}
          {fatAlpha > 0.02 && (
            <>
              <ellipse cx={cx - waist * 0.55} cy="178" rx="14" ry="22" fill="#a8741f" opacity={fatAlpha * 0.22} />
              <ellipse cx={cx + waist * 0.55} cy="178" rx="14" ry="22" fill="#a8741f" opacity={fatAlpha * 0.22} />
            </>
          )}
          {/* waist callout */}
          <g opacity="0.9">
            <line x1={cx - waist - 16} y1="154" x2={cx - waist - 4} y2="154" stroke="#475569" strokeWidth="1.5" />
            <line x1={cx + waist + 4} y1="154" x2={cx + waist + 16} y2="154" stroke="#475569" strokeWidth="1.5" />
            <text x={cx} y="262" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>WAIST</text>
          </g>
        </>
      )}
      {ghostPath && (
        <path d={ghostPath} fill="none" stroke="#15803d" strokeWidth="2.5" strokeDasharray="5 4" opacity="0.9" />
      )}
    </svg>
  );
}

function LateralView({ bcs, species, stroke, fill }) {
  return (
    <svg viewBox="0 0 230 140" className="w-full h-full">
      {/* legs */}
      <rect x="70" y="100" width="9" height="30" rx="4" fill={fill} stroke={stroke} strokeWidth="2" />
      <rect x="150" y="100" width="9" height="30" rx="4" fill={fill} stroke={stroke} strokeWidth="2" />
      <path d={lateralPath(bcs)} fill={fill} stroke={stroke} strokeWidth="3" strokeLinejoin="round" />
      <circle cx="50" cy="64" r="2.4" fill={stroke} />
      <text x="120" y="134" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>
        {bcs <= 4 ? "ABDOMINAL TUCK" : bcs >= 6 ? "ABDOMINAL SAG" : "SLIGHT TUCK"}
      </text>
    </svg>
  );
}

const RefThumb = ({ bcs, species }) => (
  <div className="flex flex-col items-center">
    <div className="w-12 h-16 opacity-70">
      <TopDownView bcs={bcs} species={species} stroke="#94a3b8" fill={SPECIES_FILL[species] || SPECIES_FILL.dog} cues={false} />
    </div>
    <div className="text-[9px] font-bold text-slate-500 mt-0.5">{bcs}</div>
    <div className="text-[8px] text-slate-400 uppercase tracking-wide">{BCS_LABELS[bcs]}</div>
  </div>
);

/* Interactive body-condition model. `value` is 1–9 (or null until set). */
export const BodyConditionModel = ({ value, onChange, correct, revealed, species = "dog" }) => {
  const fill = SPECIES_FILL[species] || SPECIES_FILL.dog;
  const selected = value != null;
  const shown = value ?? 5;
  const isCorrect = revealed && value === correct;
  const stroke = !selected
    ? "#94a3b8"
    : revealed
    ? isCorrect ? "#15803d" : "#dc2626"
    : BAND[bandOf(value)].color;

  return (
    <div className="rounded-lg border-2 border-slate-200 bg-white p-4" data-testid="bcs-body-model">
      {/* reference scale */}
      <div className="flex items-center justify-between gap-2 pb-3 mb-3 border-b border-slate-100">
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Reference</span>
        <div className="flex items-end gap-4">
          {[1, 5, 9].map((n) => <RefThumb key={n} bcs={n} species={species} />)}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
        {/* the patient — top-down + lateral, morphing with the chosen score */}
        <div className="flex items-center gap-2 justify-center">
          <div className="w-28 h-40" data-testid="bcs-topdown">
            <TopDownView
              bcs={shown}
              species={species}
              stroke={stroke}
              fill={selected ? fill : "#eef2f6"}
              ghostPath={revealed && !isCorrect ? topDownPath(correct) : null}
            />
          </div>
          <div className="w-32 h-24" data-testid="bcs-lateral">
            <LateralView bcs={shown} species={species} stroke={stroke} fill={selected ? fill : "#eef2f6"} />
          </div>
        </div>

        {/* the control */}
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold font-mono-clinical" style={{ color: selected ? stroke : "#cbd5e1" }} data-testid="bcs-readout">
              {selected ? value : "–"}
            </span>
            <span className="text-sm text-slate-400 font-semibold">/ 9</span>
            {selected && (
              <span className={`text-sm font-semibold ${revealed ? (isCorrect ? "text-emerald-600" : "text-rose-600") : BAND[bandOf(value)].text}`} data-testid="bcs-label">
                {BCS_LABELS[value]}
              </span>
            )}
          </div>

          <input
            type="range" min="1" max="9" step="1"
            value={shown}
            disabled={revealed}
            onChange={(e) => onChange(Number(e.target.value))}
            data-testid="bcs-slider"
            className="w-full mt-3 accent-[#15803d] disabled:opacity-60 cursor-pointer"
            style={{ accentColor: stroke }}
          />
          {/* discrete ticks (also act as quick-pick buttons) */}
          <div className="flex justify-between gap-1 mt-2">
            {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => {
              const on = value === n;
              return (
                <button
                  key={n}
                  type="button"
                  disabled={revealed}
                  onClick={() => onChange(n)}
                  data-testid={`bcs-btn-${n}`}
                  className={`flex-1 h-7 rounded text-[11px] font-bold font-mono-clinical border transition-colors ${
                    revealed && n === correct
                      ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                      : revealed && on
                      ? "border-rose-400 bg-rose-50 text-rose-600"
                      : on
                      ? "border-transparent text-white"
                      : "border-slate-200 bg-white text-slate-500 hover:border-slate-400"
                  }`}
                  style={on && !revealed ? { backgroundColor: stroke } : undefined}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-slate-400 mt-1.5 font-semibold uppercase tracking-wide">
            <span>Underweight</span><span>Ideal</span><span>Overweight</span>
          </div>

          {revealed && (
            <div className={`mt-3 flex items-center gap-2 text-sm font-semibold ${isCorrect ? "text-emerald-600" : "text-rose-600"}`} data-testid="bcs-feedback">
              {isCorrect ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {isCorrect ? `Correct — BCS ${correct}/9` : `Ideal answer: BCS ${correct}/9 (${BCS_LABELS[correct]})`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
